import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Sparkles } from 'lucide-react';
import ChatMessage from './ChatMessage';
import SystemMessage from './SystemMessage';
import type { ChatMessage as ChatMessageType } from '@/types/intelliHealthChat';

export interface DisplayMessage {
  id: string;
  type: 'human' | 'ai' | 'system';
  content: string;
}

interface MessageListProps {
  messages: ChatMessageType[];
  streamingMessage: string;
  isStreaming: boolean;
  isWaitingForResponse: boolean;
  systemMessages: DisplayMessage[];
  /** Callback for inline sync button in AI messages that detect "Force Sync" prompts. */
  onSyncPatient?: () => Promise<void>;
}

const SCROLL_THRESHOLD = 100;

const MessageList: React.FC<MessageListProps> = ({
  messages,
  streamingMessage,
  isStreaming,
  isWaitingForResponse,
  systemMessages,
  onSyncPatient,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);

  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      setIsUserScrolledUp(false);
    }
  }, []);

  // Auto-scroll on new messages / streaming unless user scrolled up
  useEffect(() => {
    if (!isUserScrolledUp) {
      const el = scrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }, [messages, streamingMessage, isWaitingForResponse, isUserScrolledUp]);

  const handleScroll = useCallback(() => {
    setIsUserScrolledUp(!isNearBottom());
  }, [isNearBottom]);

  // Build combined message list with system messages interleaved by timestamp
  const allMessages: DisplayMessage[] = [];
  let sysIdx = 0;

  messages.forEach((msg, i) => {
    // Insert system messages whose timestamp <= this message's created_at
    while (sysIdx < systemMessages.length) {
      const sysTimestamp = parseInt(systemMessages[sysIdx].id.split('-')[1] || '0');
      const msgTimestamp = msg.created_at ? new Date(msg.created_at).getTime() : 0;
      if (sysTimestamp <= msgTimestamp) {
        allMessages.push(systemMessages[sysIdx]);
        sysIdx++;
      } else {
        break;
      }
    }
    allMessages.push({
      id: `msg-${i}`,
      type: msg.type,
      content: msg.content,
    });
  });
  // Append remaining system messages at the end
  while (sysIdx < systemMessages.length) {
    allMessages.push(systemMessages[sysIdx]);
    sysIdx++;
  }

  return (
    <div className="relative flex-1 overflow-hidden">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto px-4 py-3 space-y-3"
      >
        {allMessages.map((msg) => {
          if (msg.type === 'system') {
            return <SystemMessage key={msg.id} text={msg.content} />;
          }
          return (
            <ChatMessage
              key={msg.id}
              type={msg.type}
              content={msg.content}
              onSyncPatient={msg.type === 'ai' ? onSyncPatient : undefined}
            />
          );
        })}

        {/* Streaming message */}
        {isStreaming && streamingMessage && (
          <ChatMessage type="ai" content={streamingMessage} isStreaming />
        )}

        {/* Typing indicator when waiting for response or streaming hasn't produced text yet */}
        {(isWaitingForResponse || (isStreaming && !streamingMessage)) && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2 justify-start"
          >
            <div className="w-6 h-6 rounded-full bg-brand-teal-light flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3 h-3 text-brand-teal" />
            </div>
            <div className="rounded-xl px-4 py-3 bg-white border border-slate-200">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-2 h-2 rounded-full bg-brand-teal"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.4, 1, 0.4],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {isUserScrolledUp && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white border border-slate-200 shadow-sm rounded-full p-1.5 hover:bg-slate-50 transition-colors"
        >
          <ChevronDown className="w-4 h-4 text-slate-500" />
        </button>
      )}
    </div>
  );
};

export default MessageList;
